import React from 'react'
import CustomRow from '../../reusable-components/CustomRow'
import { Button, Card, Col, Space, Table, Tooltip, theme } from 'antd'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../core/utils/reducer'
import CoreEnums from '../../../core/utils/enums'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faUsers } from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA } from '../../lib/utils'
import CommonPlanTemplateForm from './PlanTemplateForm'

// Import specialized plan template forms
const getPlanTemplateForm = (planType) => {
  // For prevention plans, return the specialized component
  if (planType === 'preventive') {
    try {
      // Try to dynamically import the specialized form
      const PreventivePlanTemplateForm = require('../PreventivePlans/components/PlanTemplateForm').default
      return PreventivePlanTemplateForm
    } catch (e) {
      console.error('Failed to load specialized plan template form:', e)
      // Fallback to common form if specialized one is not available
      return CommonPlanTemplateForm
    }
  }

  // For rehabilitation plans, return the specialized component
  if (planType === 'rehabilitation') {
    try {
      // Try to dynamically import the specialized form
      const RehabilitationPlanTemplateForm = require('../RehabilitationPlans/components/PlanTemplateForm').default
      return RehabilitationPlanTemplateForm
    } catch (e) {
      console.error('Failed to load specialized rehabilitation plan template form:', e)
      // Fallback to common form if specialized one is not available
      return CommonPlanTemplateForm
    }
  }

  // For wellness plans, return the specialized component
  if (planType === 'wellness') {
    try {
      // Try to dynamically import the specialized form
      const WellnessPlanTemplateForm = require('../WellnessPlans/components/PlanTemplateForm').default
      return WellnessPlanTemplateForm
    } catch (e) {
      console.error('Failed to load specialized wellness plan template form:', e)
      // Fallback to common form if specialized one is not available
      return CommonPlanTemplateForm
    }
  }

  // For meal plans, return the specialized component
  if (planType === 'meal') {
    try {
      // Try to dynamically import the specialized form
      const MealPlanTemplateForm = require('../Meal Plans/components/PlanTemplateForm').default
      return MealPlanTemplateForm
    } catch (e) {
      console.error('Failed to load specialized meal plan template form:', e)
      // Fallback to common form if specialized one is not available
      return CommonPlanTemplateForm
    }
  }

  // For other plan types, use Common form for now
  // In a full implementation, this would check for all plan types
  return CommonPlanTemplateForm
}

// Registry of tab keys for different plan types
const getPlanTabKey = (planType, isMonitoring) => {
  const planTabKeys = {
    care: isMonitoring ? CoreEnums.tabKeys.MONITORING_PLANS : CoreEnums.tabKeys.CARE_PLANS,
    treatment: 'TREATMENT_PLANS',
    nutrition: 'NUTRITION_PLANS',
    wellness: 'WELLNESS_PLANS',
    preventive: 'PREVENTIVE_PLANS',
    rehabilitation: 'REHABILITATION_PLANS',
    weight: 'WEIGHT_PLANS',
    meal: 'MEAL_PLANS'
  }

  return planTabKeys[planType] || CoreEnums.tabKeys.CARE_PLANS
}

const PlansList = ({
  planTemplates,
  dataPoints,
  handleEnrollmentView,
  refreshView,
  isMonitoring,
  planType,
  planLabel
}) => {
  const dispatch = useDispatch()
  const { token } = theme.useToken()

  const openPlanForm = (isNew, recordData) => {
    const tabKey = getPlanTabKey(planType, isMonitoring)

    // Get the appropriate PlanTemplateForm based on the plan type
    const PlanTemplateForm = getPlanTemplateForm(planType)

    dispatch(
      coreReducer.actions.addTab({
        key: isNew ? `${tabKey}_new` : `${tabKey}_${recordData?._id}`,
        closable: true,
        label: isNew ? `New ${planLabel || 'Plan'}` : `Edit ${recordData?.name || planLabel || 'Plan'}`,
        children: (
          <>
            <PlanTemplateForm
              isMonitoring={isMonitoring}
              isNew={isNew}
              recordData={isNew ? null : recordData}
              dataPoints={dataPoints}
              refreshView={refreshView}
              planType={planType}
              planLabel={planLabel}
            />
          </>
        )
      })
    )
  }

  return (
    <CustomRow>
      <Col span={24}>
        <Card
          headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
          title={isMonitoring ? `Monitoring ${planLabel || 'Plans'}` : planLabel || 'Plans'}
          size='small'
          extra={
            <Button
              onClick={() => {
                openPlanForm(true)
              }}
            >
              New
            </Button>
          }
        >
          <Table
            size='small'
            bordered
            dataSource={planTemplates}
            columns={[
              {
                title: 'Name',
                key: 'name',
                dataIndex: 'name'
              },
              {
                title: 'Actions',
                render: (record) => {
                  return (
                    <Space wrap>
                      <Tooltip title='Edit'>
                        <FontAwesomeIcon
                          fontSize={20}
                          size='lg'
                          id='cancel-booking'
                          icon={faEdit}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = token.colorPrimary
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = '#ccc'
                          }}
                          style={{ color: '#ccc', transition: 'color 300ms ease' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            openPlanForm(false, record)
                          }}
                        />
                      </Tooltip>
                      <Tooltip title='Enrollment'>
                        <FontAwesomeIcon
                          fontSize={20}
                          size='lg'
                          icon={faUsers}
                          onMouseOver={(e) => {
                            e.currentTarget.style.color = token.colorPrimary
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.color = '#ccc'
                          }}
                          style={{ color: '#ccc', transition: 'color 300ms ease' }}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEnrollmentView(record)
                          }}
                        />
                      </Tooltip>
                    </Space>
                  )
                }
              }
            ]}
          />
        </Card>
      </Col>
    </CustomRow>
  )
}

export default PlansList
